import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Component, Inject, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subject, combineLatest, of, catchError, filter, switchMap } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { Product } from '@spartacus/core';

import { IDocument, SdDocumentsComponent } from '@seb/web-design-system/documents';
import { ProductAccessoryTypeEnum } from '@seb/web-common/core/enums';

import {
  IComponentWrapper, IconSizeEnum, IHeading, SdBannerComponent, SdButtonComponent, SdComponentWrapperComponent,
  SdFormErrorComponent, SdFormInputComponent, SdHeadingComponent, SdIconComponent, SdLoaderComponent,
  SdModalDefaultComponent, SizesEnum
} from '@seb/web-design-system/core';

import { BaseCrossSellComponent } from '../../components/base/base-cross-sell/base-cross-sell.component';
import { AbstractPageComponent } from '../../templates/abstract-page/abstract-page.component';
import { DamResponse, LameConfig, WC_CONFIG_PROVIDER, WcConfig } from '../../models';
import { AccessoryCrossSellService, RepairabilityService } from '../../services';
import { DocumentMapper, HeadingParagraphMapper } from '../../mappers';

@Component({
  selector: 'wc-compatibility-page',
  templateUrl: './compatibility-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdHeadingComponent,
    FormsModule,
    ReactiveFormsModule,
    SdFormInputComponent,
    SdFormErrorComponent,
    SdButtonComponent,
    SdLoaderComponent,
    SdIconComponent,
    SdComponentWrapperComponent,
    SdDocumentsComponent,
    SdModalDefaultComponent,
    SdBannerComponent,
    BaseCrossSellComponent,
    TranslatePipe
  ]
})
export class CompatibilityPageComponent extends AbstractPageComponent implements OnInit {
  documentsWrapperContent: IComponentWrapper;
  accessoriesWrapperContent: IComponentWrapper;
  detachedPiecesWrapperContent: IComponentWrapper;
  accessories: Product[] = [];
  detachedPieces: Product[] = [];
  productCodes: DamResponse[] = [];
  pageSize = 6;
  pageAccessories = 0;
  pageDetachedPieces = 0;
  detachedPiecesPage = 0;
  accessoriesPiecesPage = 0;
  accessoriesTotalPages: number;
  detachedPiecesTotalPages: number;
  lameConfig: LameConfig;
  documents: IDocument[];
  formGroup: FormGroup;
  isLoading = false;
  isRefModalOpened = false;
  isSavSearch = false;
  isEmptyProduct = -1;
  refSav = '';
  urlImage = '/assets/images/asset-reparability.jpg';
  heading: IHeading;
  iconSize = IconSizeEnum;
  readonly context = 'Cross-sell';

  private savSubject: Subject<string> = new Subject<string>();
  private accessoriesSearchPageSubject: Subject<number> = new Subject<number>();
  private detachedPiecesSearchPageSubject: Subject<number> = new Subject<number>();

  constructor(
    private repairabilityService: RepairabilityService,
    private docMapper: DocumentMapper,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private accessoryCrossSellService: AccessoryCrossSellService,
    private cdRef: ChangeDetectorRef,
    private headingParagraphMapper: HeadingParagraphMapper,
    @Inject(WC_CONFIG_PROVIDER) private wcConfig: WcConfig
  ) {
    super();

    this.heading = {
      title: this.translateService.instant('product.reparability.title'),
      subtitle: this.translateService.instant('product.reparability.subtitle')
    };
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.lameConfig = this.wcConfig?.pdp?.accessories;
    const richParagraphDataDocuments = this.createRichParagraphData(this.translateService.instant('product.reparability.titles.documents'));

    this.documentsWrapperContent = {
      titleLevel: 2,
      links: [],
      footNotes: [],
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphDataDocuments)
    };
    const richParagraphDataAccessories = this.createRichParagraphData(this.translateService.instant('product.reparability.titles.accessories'));

    this.accessoriesWrapperContent = {
      titleLevel: 2,
      links: [],
      footNotes: [],
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphDataAccessories)
    };

    const richParagraphDataDetachedPieces = this.createRichParagraphData(this.translateService.instant('product.reparability.titles.detachedPieces'));

    this.detachedPiecesWrapperContent = {
      ...this.accessoriesWrapperContent,
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphDataDetachedPieces)
    };

    this.formGroup = this.formBuilder.group({
      search: new FormControl('', {
        validators: [Validators.required, Validators.minLength(+5)],
        updateOn: 'submit'
      })
    });

    this.subscriptions.add(
      this.savSubject.pipe(
        filter((codeSAV) => !!codeSAV),
        switchMap((value) => {
          this.isLoading = true;
          this.isSavSearch = false;
          return combineLatest([
            this.repairabilityService.getDocumentsFromRepairability(value),
            this.repairabilityService.getCmmfListForCode(value).pipe(catchError(() => of(undefined)))
          ]);
        })
      ).subscribe(([bynderDocuments, res]) => {
        this.detachedPiecesPage = 0;
        this.accessoriesPiecesPage = 0;
        this.isEmptyProduct = 0;

        if (bynderDocuments.length !== 0) {
          this.documents = this.docMapper.getBynderDocumentToSortedDocuments(bynderDocuments);
          this.isEmptyProduct++;
        }

        if (res?.length) {
          this.productCodes = [...res];
          this.accessoriesSearchPageSubject.next(this.accessoriesPiecesPage);
          this.detachedPiecesSearchPageSubject.next(this.detachedPiecesPage);
        } else {
          this.allSearchDone();
        }

        this.isSavSearch = true;
        this.cdRef.markForCheck();
      })
    );

    const accessoriesSearch$ = this.accessoriesSearchPageSubject.asObservable().pipe(
      switchMap((page) => {
        return this.productCodes[0]?.accessories?.length > 0
          ? this.getAccessoriesOfType(this.productCodes[0]?.accessories, page, this.pageSize, ProductAccessoryTypeEnum.ACCESSORY)
          : of(undefined);
      })
    );

    const detachedPiecesSearch$ = this.detachedPiecesSearchPageSubject.pipe(
      switchMap((page) => {
        return this.productCodes[0]?.spareParts?.length > 0
          ? this.getAccessoriesOfType(this.productCodes[0]?.spareParts, page, this.pageSize, ProductAccessoryTypeEnum.DETACHED_PIECE)
          : of(undefined);
      })
    );

    this.subscriptions.add(
      combineLatest([
        accessoriesSearch$,
        detachedPiecesSearch$
      ]).subscribe(([accessories, detachedPieces]) => {
        if (accessories) {
          if (!this.accessoriesTotalPages || accessories?.pagination.totalPages !== this.accessoriesTotalPages) {
            this.accessoriesTotalPages = accessories?.pagination?.totalPages;
          }
          this.accessories =
            this.pageAccessories === 0
              ? accessories?.products
              : // eslint-disable-next-line no-unsafe-optional-chaining
              [...this.accessories?.filter((accessory) => Object.values(accessory).length), ...accessories?.products];
          if (this.accessoriesTotalPages > this.pageAccessories + 1) {
            this.accessories.push({});
          }
        }
        if (detachedPieces) {
          if (
            !this.detachedPiecesTotalPages ||
            detachedPieces?.pagination.totalPages !== this.detachedPiecesTotalPages
          ) {
            this.detachedPiecesTotalPages = detachedPieces?.pagination?.totalPages;
          }
          this.detachedPieces =
            this.pageDetachedPieces === 0
              ? detachedPieces?.products
              : // eslint-disable-next-line no-unsafe-optional-chaining
              [...this.detachedPieces?.filter((pieces) => Object.values(pieces).length), ...detachedPieces?.products];
          if (this.detachedPiecesTotalPages > this.pageDetachedPieces + 1) {
            this.detachedPieces.push({});
          }
        }
        if (this.detachedPieces?.length > 0 || this.accessories?.length > 0) {
          this.isEmptyProduct++;
        }
        this.allSearchDone();
      })
    );
  }

  private allSearchDone(): void {
    this.isLoading = false;
    if (this.isEmptyProduct === 0) {
      this.formGroup.get('search').setErrors({
        notExist: true
      });
    }
    this.cdRef.markForCheck();
  }

  onDetachedPiecesNext(): void {
    this.loadNextDetachedPiecesPage(++this.detachedPiecesPage);
  }

  onAccessoriesNext(): void {
    this.loadNextAccessoriesPage(++this.accessoriesPiecesPage);
  }

  loadNextDetachedPiecesPage(page: number): void {
    this.pageDetachedPieces = page;
    if (this.detachedPiecesTotalPages <= page) {
      return;
    }
    this.detachedPiecesSearchPageSubject.next(page);
  }

  loadNextAccessoriesPage(page: number): void {
    this.pageAccessories = page;
    if (this.accessoriesTotalPages > page) {
      this.accessoriesSearchPageSubject.next(page);
    }
  }

  clear(): void {
    const formControl = this.formGroup.get('search');
    formControl.markAsUntouched();
    formControl.setValue('');
  }

  inputChanged(): void {
    this.formGroup.get('search').markAsUntouched();
  }

  onSubmit(): void {
    const formControl = this.formGroup.get('search');
    formControl.markAsTouched();
    if (!formControl.invalid && this.prepareInput(formControl) !== this.refSav) {
      delete this.documents;
      delete this.accessories;
      delete this.detachedPieces;
      this.pageAccessories = 0;
      this.pageDetachedPieces = 0;
      this.savSubject.next(this.prepareInput(formControl));
    }
    this.refSav = this.prepareInput(formControl);
  }

  openModalRef(): void {
    this.isRefModalOpened = true;
  }

  private getAccessoriesOfType(productCodes, page, pageSize, accessoriesType: ProductAccessoryTypeEnum) {
    return this.accessoryCrossSellService
      .getAccessoriesForProductCodes(productCodes, page, pageSize, accessoriesType)
      .pipe(filter((p) => !!p));
  }

  private prepareInput(formControl): string {
    return formControl?.value?.trim();
  }

  private createRichParagraphData(titre) {
    return {
      heading: {
        headline: this.translateService.instant(titre)
      },
      titleTheme: this.lameConfig?.titleTheme,
      isUnderlined: this.lameConfig?.titleUnderlined,
      size: SizesEnum.l
    };
  }
}
